/* Camps & Trips index: /trips
   ----------------------------------------------------------------------
   Rebuilt 2026-10-01 from the owner's "Kidsology Camps and Trips Catalog
   2026/27" (v1.5) plus Spain, Italy and Switzerland, on the brief:
   "a clean, beautiful structure but a very simple one: accessible,
   user-friendly, easy to navigate, easy to understand". The same
   structure is meant for Programs and for Career Pathways & Internships
   next, so the layout lives in src/components/catalog.

   Page order (owner's revision, 2026-10-01): hero → the Dubai camp,
   featured on its own → all camps and trips, the UK first as the most
   recent, closing with the "somewhere else" tile → the most recent
   camp's results → what makes a Kidsology camp → safety → how to book
   → enquiry form. Every item links to its own page with the same
   structure (src/pages/trips/CampPage.jsx). */

import { SIZES } from "../lib/img";
import React from "react";
/* Not used by this page, kept on purpose: CRA emits one stylesheet in
   import order, and the Dubai and UK recap pages depend on Trips.css
   landing before TripDetail.css, as it did when this page used it. */
import "./Home.css";
import "./Programs.css";
import "./Trips.css";
import SEO from "../components/SEO";
import SiteHeader from "../components/SiteHeader";
import LocaleLink from "../components/LocaleLink";
import { useLocale, useL } from "../i18n/LocaleContext";
import { CAMPS_COPY, CAMP_BY_SLUG, FEATURED_CAMP, ALL_TRIPS } from "../data/camps";
import { tripsListSchema } from "../data/schemas";
import { Pic, FloatingCta, useHashScroll } from "../components/catalog/CatalogParts";
import {
  CampGrid, FeaturedCamp, ResultsBand, MethodSection, SafetySection, BookingSection, EnquirySection,
} from "./trips/TripBlocks";
import { track } from "../lib/analytics";

/* Four destinations beside the hero: one per corner of the catalogue. */
const HERO_PHOTOS = [
  CAMP_BY_SLUG["uk-summer"].photos[0],
  CAMP_BY_SLUG["istanbul-bursa"].photos[1],
  CAMP_BY_SLUG["tokyo-osaka"].photos[0],
  CAMP_BY_SLUG["zurich-geneva"].photos[1],
];

export default function Trips() {
  const { locale } = useLocale();
  const copy = useL(CAMPS_COPY);
  const photos = useL(HERO_PHOTOS);
  const uk = useL(CAMP_BY_SLUG["uk-summer"]);
  useHashScroll();

  return (
    <div className="cat-page">
      <SEO
        title={copy.seo.title}
        description={copy.seo.description}
        path="/trips"
        image={`${HERO_PHOTOS[1].src}.jpg`}
        schema={tripsListSchema([FEATURED_CAMP, ...ALL_TRIPS], locale)}
      />
      <SiteHeader ctaHref="#enquire" />
      <main id="main">
        {/* ── Hero ── */}
        <header className="cat-hero">
          <div className="cat-hero-inner">
            <div className="cat-hero-copy">
              <p className="cat-eyebrow">{copy.eyebrow}</p>
              <h1 className="cat-title">{copy.title}</h1>
              <p className="cat-lead">{copy.lead}</p>
              <ul className="cat-stats">
                {copy.stats.map((s, i) => (
                  <li key={i}>
                    {/* "50+" has no letters, so pin it left to right or Arabic shows "+50". */}
                    <span className="cat-stat-v" dir={/\p{L}/u.test(s.v) ? undefined : "ltr"}>{s.v}</span>
                    <span className="cat-stat-l">{s.l}</span>
                  </li>
                ))}
              </ul>
              <div className="cat-actions cat-hero-actions">
                <a href="#all-trips" className="btn btn-gold-lg" onClick={() => track("trips_hero_cta_click", { target: "all-trips" })}>{copy.seeAll}</a>
                <a href="#enquire" className="btn btn-outline-white" onClick={() => track("trips_hero_cta_click", { target: "enquire" })}>{copy.enquiry.title}</a>
              </div>
            </div>
            <div className="cat-mosaic">
              {photos.map((p, i) => <Pic key={p.src} src={p.src} alt={p.alt} eager={i < 2} sizes={SIZES.strip} />)}
            </div>
          </div>
        </header>

        {/* ── The featured camp ── */}
        <section className="cat-section cat-section--featured" id="featured" aria-label={copy.eyebrow}>
          <div className="cat-wrap">
            <FeaturedCamp camp={FEATURED_CAMP} />
          </div>
        </section>

        {/* ── All camps and trips ── */}
        <section className="cat-section cat-section--flush" id="all-trips" aria-labelledby="all-title">
          <div className="cat-wrap">
            <h2 id="all-title" className="cat-h2">{copy.allTitle}</h2>
            <p className="cat-sub">{copy.allSub}</p>
            <CampGrid camps={ALL_TRIPS} anywhere />
          </div>
        </section>

        {/* ── The most recent camp, in numbers ── */}
        <ResultsBand
          results={uk.results}
          eyebrow={copy.results.eyebrow}
          title={copy.results.title}
          link={<LocaleLink to={uk.pastHref}>{uk.pastLabel}</LocaleLink>}
        />

        <MethodSection />
        <SafetySection />
        <BookingSection />
        <EnquirySection />
      </main>
      <FloatingCta
        href="#enquire"
        label={copy.enquiry.title}
        heroSelector=".cat-hero-actions"
        targetId="enquire"
        event="trips_float_cta_click"
      />
    </div>
  );
}
